/** @forge-header
 * @id          nbaParsers
 * @role        NBA ticker parsers | game moneyline + season props
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | nbaConsolidated/teamNames:teamNameFromCode
 * @downstream  kalshiTicker/parse
 * @exports     parseNbaGame:fn | parseNbaSeason:fn | NbaPropStat:type | NBA_WIRE_PROP_STAT | NBA_PROP_SERIES | NBA_PROP_STATS | NBA_PROP_STAT_LABELS | NBA_PROP_BINARY_THRESHOLD | NBA_PROP_BINARY_WORDING
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback } from '../helpers';
import { teamNameFromCode } from '../teamNames';

/** NBA game moneyline: KXNBAGAME-26JAN15BOSMIA-BOS */
export function parseNbaGame(raw: string): ParsedTicker {
  const parts = raw.includes('--') ? raw.split('--') : raw.split('-');

  let eventPart: string;
  let teamCode: string | null = null;

  if (raw.includes('--')) {
    eventPart = parts[0] ?? '';
    teamCode = parts[1] ?? null;
  } else {
    teamCode = parts[parts.length - 1] ?? null;
    eventPart = parts.slice(0, -1).join('-');
  }

  const eventParts = eventPart.split('-');
  const mid = eventParts[1] ?? '';

  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);

  if (teamCodes.length !== 6) {
    return parseFallback(raw);
  }

  const awayCode = teamCodes.slice(0, 3);
  const homeCode = teamCodes.slice(3, 6);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: teamNameFromCode(awayCode, 'nba'),
    homeName: teamNameFromCode(homeCode, 'nba'),
  };

  const shortLabel = `NBA: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `NBA: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode} · ${teamCode} Win`
    : `NBA: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}`;

  return {
    raw,
    category: 'nba-game',
    series: 'KXNBAGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}

/** NBA season props: KXNBA-26-SAS */
export function parseNbaSeason(raw: string): ParsedTicker {
  const parts = raw.split('-');
  const year = parts[1] ? `20${parts[1]}` : null;
  const outcome = parts[2] ?? null;

  const shortLabel = outcome ? `NBA Season: ${outcome}` : 'NBA Season Prop';
  const fullLabel = year ? `NBA ${year} Season${outcome ? ` · ${outcome}` : ''}` : shortLabel;

  return {
    raw,
    category: 'nba-season',
    series: 'KXNBA',
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

// ── NBA player props (nba-player-props; NBAALT since 2026-10-07) ──────────
//
// DATA ONLY — the NBA's entry in the shared prop vocabulary
// (lib/sport/propStat `PROP_VOCABULARY`). The wire selects and names each row
// (`class: 'player-prop'` + `stat`, with `player` / `playerTeam` /
// `playerKey`); nothing here parses a ticker. The ids are namespaced
// (`nba-…`) because the wire's `pts` / `ast` are also the NHL spellings, and
// `PropStat` ids must stay disjoint: a synth key carries the stat id with no
// sport tag.

export type NbaPropStat =
  | 'nba-pts' | 'nba-reb' | 'nba-ast' | 'nba-3pt' | 'nba-pra' | 'nba-pr' | 'nba-pa' | 'nba-ra'
  | 'nba-blk' | 'nba-stl' | 'nba-stocks' | 'nba-ftm'
  | 'nba-double-double' | 'nba-triple-double' | 'nba-first-basket' | 'nba-pts-leader';

/** Wire `stat` (hydracore HYDRA_API.md § Player props, NBA vocabulary) → g1 id.
 *  A stat outside this table is ignored, never guessed (rule 8). */
export const NBA_WIRE_PROP_STAT: Readonly<Record<string, NbaPropStat>> = {
  pts: 'nba-pts',
  reb: 'nba-reb',
  ast: 'nba-ast',
  '3pt': 'nba-3pt',
  pra: 'nba-pra',
  pr: 'nba-pr',
  pa: 'nba-pa',
  ra: 'nba-ra',
  blk: 'nba-blk',
  stl: 'nba-stl',
  stocks: 'nba-stocks',
  ftm: 'nba-ftm',
  'double-double': 'nba-double-double',
  'triple-double': 'nba-triple-double',
  'first-basket': 'nba-first-basket',
  'pts-leader': 'nba-pts-leader',
};

/** Kalshi series (exact segment) → g1 id, per the same contract paragraph:
 *  what a ticker alone says before its frame arrives (a held position, a deep
 *  link). Both STOCK spellings are catalogued as `stocks`. */
export const NBA_PROP_SERIES: Readonly<Record<string, NbaPropStat>> = {
  KXNBAPTS: 'nba-pts',
  KXNBAREB: 'nba-reb',
  KXNBAAST: 'nba-ast',
  KXNBA3PT: 'nba-3pt',
  KXNBAPRA: 'nba-pra',
  KXNBAPR: 'nba-pr',
  KXNBAPA: 'nba-pa',
  KXNBARA: 'nba-ra',
  KXNBABLK: 'nba-blk',
  KXNBASTL: 'nba-stl',
  KXNBASTOCK: 'nba-stocks',
  KXNBASTOCKS: 'nba-stocks',
  KXNBAFTM: 'nba-ftm',
  KXNBA2D: 'nba-double-double',
  KXNBA3D: 'nba-triple-double',
  KXNBAFIRSTBASKET: 'nba-first-basket',
  KXNBAPTSLEADER: 'nba-pts-leader',
};

/** Stat chip order: the scoring ladders, the combos, the defensive ladders,
 *  then the no-line families. */
export const NBA_PROP_STATS: readonly NbaPropStat[] = [
  'nba-pts', 'nba-reb', 'nba-ast', 'nba-3pt',
  'nba-pra', 'nba-pr', 'nba-pa', 'nba-ra',
  'nba-blk', 'nba-stl', 'nba-stocks', 'nba-ftm',
  'nba-double-double', 'nba-triple-double', 'nba-first-basket', 'nba-pts-leader',
];

/** Kalshi's own family names (the row titles' "…: Threes") where it lists them. */
export const NBA_PROP_STAT_LABELS: Record<NbaPropStat, string> = {
  'nba-pts': 'Points',
  'nba-reb': 'Rebounds',
  'nba-ast': 'Assists',
  'nba-3pt': 'Threes',
  'nba-pra': 'Pts + Reb + Ast',
  'nba-pr': 'Pts + Reb',
  'nba-pa': 'Pts + Ast',
  'nba-ra': 'Reb + Ast',
  'nba-blk': 'Blocks',
  'nba-stl': 'Steals',
  'nba-stocks': 'Steals + Blocks',
  'nba-ftm': 'Free Throws Made',
  'nba-double-double': 'Double Double',
  'nba-triple-double': 'Triple Double',
  'nba-first-basket': 'First Basket',
  'nba-pts-leader': 'Points Leader',
};

/** The no-line families: one yes/no leg per player. Like the NFL touchdown
 *  binaries each gets a FIXED identity threshold so the numeric `PropRung` /
 *  synth-key contract holds; the wording never prints it. The double-double
 *  rows carry `floorStrike` 0.5 — that is not a line, and this wins over it. */
export const NBA_PROP_BINARY_THRESHOLD: Partial<Record<NbaPropStat, number>> = {
  'nba-double-double': 1,
  'nba-triple-double': 1,
  'nba-first-basket': 1,
  'nba-pts-leader': 1,
};

/** The two sides of each no-line family, worded after the player's name. */
export const NBA_PROP_BINARY_WORDING: Partial<Record<NbaPropStat, { yes: string; no: string }>> = {
  'nba-double-double': { yes: 'records a double-double', no: 'does not record a double-double' },
  'nba-triple-double': { yes: 'records a triple-double', no: 'does not record a triple-double' },
  'nba-first-basket': { yes: 'scores the first basket', no: 'does not score the first basket' },
  'nba-pts-leader': { yes: 'leads the game in points', no: 'does not lead the game in points' },
};
